import React, { useEffect, useMemo } from "react";
import { useGetMemberAvailableRailsQuery } from "../../../store/member/member-contribution";
import { isSocietyBankMode } from "../../../utils/memberOnlinePayment";

const RAIL_COPY = {
  paystack: {
    title: "Pay with card (online)",
    subtitle: "Redirects to secure Paystack checkout",
  },
  alatpay: {
    title: "Pay with ALATPay",
    subtitle: "Card, transfer, or USSD via ALATPay",
  },
  society_bank_transfer: {
    title: "Transfer to society account",
    subtitle: "Pay in your bank app; staff will confirm",
  },
};

export default function MemberPaymentModeChooser({
  paymentMode,
  onPaymentModeChange,
  companyBanks = [],
  selectedBankId,
  onSelectedBankIdChange,
  memberNote,
  onMemberNoteChange,
  children,
  showBankFields = true,
  compact = false,
}) {
  const { data, isLoading } = useGetMemberAvailableRailsQuery();
  const rails = useMemo(() => {
    const list = Array.isArray(data?.rails) ? data.rails.filter((r) => r && r.id) : [];
    return list;
  }, [data]);

  useEffect(() => {
    if (!rails.length) return;
    const ids = rails.map((r) => r.id);
    const current =
      paymentMode === "online"
        ? "paystack"
        : paymentMode === "bank"
        ? "society_bank_transfer"
        : paymentMode;
    if (!ids.includes(current)) {
      const first = rails[0].id;
      onPaymentModeChange(first === "society_bank_transfer" ? "bank" : first);
    }
  }, [rails, paymentMode, onPaymentModeChange]);

  const normalizedMode =
    paymentMode === "online"
      ? "paystack"
      : paymentMode === "bank"
      ? "society_bank_transfer"
      : paymentMode;

  const handleSelect = (railId) => {
    onPaymentModeChange(railId === "society_bank_transfer" ? "bank" : railId);
  };

  if (isLoading && !rails.length) {
    return <p className="small text-muted mb-3">Loading payment options…</p>;
  }

  if (!rails.length) {
    return (
      <p className="small text-danger mb-3">
        No payment methods are available. Please contact your cooperative.
      </p>
    );
  }

  return (
    <div>
      <p className={`fw-semibold text-black1 mb-2 ${compact ? "small" : ""}`}>
        How do you want to pay?
      </p>
      {!compact && (
        <p className="text-muted small mb-3">
          Choose an option enabled for your cooperative. Society transfers are
          confirmed by staff; card and ALATPay complete automatically.
        </p>
      )}
      <div className={`d-flex flex-column flex-md-row gap-${compact ? "2" : "3"} mb-4`}>
        {rails.map((rail) => {
          const copy = RAIL_COPY[rail.id] || { title: rail.label, subtitle: "" };
          const selected = normalizedMode === rail.id;
          const accent =
            rail.id === "society_bank_transfer"
              ? "var(--color-secondary)"
              : "var(--color-primary)";
          return (
            <button
              key={rail.id}
              type="button"
              className={`flex-1 rounded-[10px] border-2 ${
                compact ? "py-2 px-3" : "py-3 px-4"
              } text-start fw-semibold bg-white`}
              style={{
                borderColor: selected ? accent : "#e0e0e0",
                color: selected ? accent : "#1E1E1E",
              }}
              onClick={() => handleSelect(rail.id)}
            >
              {copy.title}
              {copy.subtitle && (
                <span className="d-block small fw-normal text-muted mt-1">
                  {copy.subtitle}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {normalizedMode === "paystack" && children}

      {normalizedMode === "alatpay" && (
        <p className="small text-muted mb-3">
          You will open ALATPay to complete payment. No extra society-account
          confirmation is needed.
        </p>
      )}

      {showBankFields && isSocietyBankMode(paymentMode) && (
        <div className="space-y-3">
          <div>
            <label
              className="form-label fw-semibold small"
              htmlFor="member-cooperative-bank"
            >
              Society account you will pay into
            </label>
            <select
              id="member-cooperative-bank"
              className="form-select"
              style={{ backgroundColor: "#fafafa", borderRadius: "5px" }}
              value={selectedBankId ? String(selectedBankId) : ""}
              onChange={(e) => onSelectedBankIdChange(e.target.value)}
            >
              <option value="">Select account</option>
              {(companyBanks || [])
                .filter((b) => b && b.id)
                .map((b) => (
                  <option key={b.id} value={String(b.id)}>
                    {(b.bank_name || "Bank") + " · " + (b.account_number || "")}
                  </option>
                ))}
            </select>
          </div>
          {typeof memberNote === "string" && onMemberNoteChange && (
            <div>
              <label
                className="form-label fw-semibold small"
                htmlFor="member-bank-note"
              >
                Note (optional)
              </label>
              <textarea
                id="member-bank-note"
                className="form-control"
                rows={2}
                placeholder="e.g. transfer reference from your bank"
                value={memberNote}
                onChange={(e) => onMemberNoteChange(e.target.value)}
                style={{ backgroundColor: "#fafafa" }}
              />
            </div>
          )}
          {!selectedBankId && (
            <p className="text-danger small mb-0">
              Select the society account to continue
            </p>
          )}
        </div>
      )}
    </div>
  );
}
